import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxRootState } from "../../shared/redux/store";
import { ReduxSliceState } from "../../shared/types";
import { withPayloadType } from "../../shared/utils";
import { PaymentLinkInfo, CompletePaymentLinkConfirmation } from "../../api/types";
import { ReduxPatientPaymentState } from "../types";

const initialState: ReduxPatientPaymentState = {
    loadState: 'READY',
    completeState: 'READY',
    linkInfo: undefined,
    confirmation: undefined,
    errorMessage: undefined,
    codeSendState: 'READY',
    codeVerifyState: 'READY',
    maskedEmail: undefined,
    codeResendCooldownSeconds: undefined,
    codeErrorMessage: undefined,
};

export const loadPaymentLink = createAction('patientPayment/loadPaymentLink', withPayloadType<string>());
export const completePaymentLink = createAction('patientPayment/completePaymentLink', withPayloadType<{ token: string; paymentIntentId: string }>());
export const sendVerificationCode = createAction('patientPayment/sendVerificationCode', withPayloadType<string>());
export const verifyCode = createAction('patientPayment/verifyCode', withPayloadType<{ token: string; code: string }>());

const patientPaymentSlice = createSlice({
    name: 'patientPayment',
    initialState,
    reducers: {
        setLoadState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.loadState = action.payload;
        },
        setCompleteState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.completeState = action.payload;
        },
        setLinkInfo: (state, action: PayloadAction<PaymentLinkInfo | undefined>) => {
            state.linkInfo = action.payload;
        },
        setConfirmation: (state, action: PayloadAction<CompletePaymentLinkConfirmation | undefined>) => {
            state.confirmation = action.payload;
        },
        setErrorMessage: (state, action: PayloadAction<string | undefined>) => {
            state.errorMessage = action.payload;
        },
        setCodeSendState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.codeSendState = action.payload;
        },
        setCodeVerifyState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.codeVerifyState = action.payload;
        },
        setMaskedEmail: (state, action: PayloadAction<string | undefined>) => {
            state.maskedEmail = action.payload;
        },
        setCodeResendCooldownSeconds: (state, action: PayloadAction<number | undefined>) => {
            state.codeResendCooldownSeconds = action.payload;
        },
        setCodeErrorMessage: (state, action: PayloadAction<string | undefined>) => {
            state.codeErrorMessage = action.payload;
        },
    },
});

export default patientPaymentSlice;

const selectSelf = (state: ReduxRootState) => state.patientPayment;
export const selectPatientPaymentIsLoading = createSelector(selectSelf, self => self.loadState === 'BUSY');
export const selectPatientPaymentLoadIsError = createSelector(selectSelf, self => self.loadState === 'ERROR');
export const selectPatientPaymentIsCompleting = createSelector(selectSelf, self => self.completeState === 'BUSY');
export const selectPatientPaymentIsComplete = createSelector(selectSelf, self => self.completeState === 'SUCCESS');
export const selectPatientPaymentLinkInfo = createSelector(selectSelf, self => self.linkInfo);
export const selectPatientPaymentConfirmation = createSelector(selectSelf, self => self.confirmation);
export const selectPatientPaymentErrorMessage = createSelector(selectSelf, self => self.errorMessage);
export const selectPatientPaymentIsSendingCode = createSelector(selectSelf, self => self.codeSendState === 'BUSY');
export const selectPatientPaymentIsVerifyingCode = createSelector(selectSelf, self => self.codeVerifyState === 'BUSY');
export const selectPatientPaymentMaskedEmail = createSelector(selectSelf, self => self.maskedEmail);
export const selectPatientPaymentCodeResendCooldownSeconds = createSelector(selectSelf, self => self.codeResendCooldownSeconds);
export const selectPatientPaymentCodeErrorMessage = createSelector(selectSelf, self => self.codeErrorMessage);
